import { ChangeDetectionStrategy, Component, inject, signal, OnInit, model } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { SystemSettingsService } from '../system-settings.service';
import { WorkingDaysResponse } from '../system-settings-models';
import { workingDays } from '../system-settings-constants';
import { CheckboxComponent } from '../../../../../ui-components/lib/checkbox/checkbox.component';
import { SelectComponent } from '../../../../../ui-components/lib/select/select.component';
import { ToasterService } from '../../../../../ui-components/services/toast/toast.service';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { TenantPathPipe } from '../../../../common/pipes/tenant-path/tenant-path.pipe';
import { ADMIN_ROUTES } from '../../../../common/constants/admin.routing.constants';

@Component({
  selector: 'app-edit-setting-working-days',
  imports: [
    ReactiveFormsModule,
    CheckboxComponent,
    SelectComponent,
    TranslocoPipe,
    RouterLink,
    TenantPathPipe,
  ],
  templateUrl: './edit-setting-working-days.component.html',
  styleUrl: './edit-setting-working-days.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EditSettingWorkingDaysComponent implements OnInit {
  ADMIN_ROUTES = ADMIN_ROUTES;

  systemSettingsService = inject(SystemSettingsService);
  translocoService = inject(TranslocoService);
  toastService = inject(ToasterService);
  router = inject(Router);
  tenantService = inject(TenantService);

  currentLang = this.translocoService.getActiveLang();
  workingDays = model<WorkingDaysResponse>();
  // Current timezone offset from UTC in hours
  currentTimezoneOffsetHours = new Date().getTimezoneOffset() / -60; // Converted to hours and inverted

  form = new FormGroup({
    days: new FormGroup({}),
    start_day: new FormControl('', Validators.required),
    started_at: new FormControl('', Validators.required),
    ended_at: new FormControl('', Validators.required),
  });
  dropdownTimeOptions: { label: string; value: string }[] = [];
  isLoading = signal<boolean>(false);

  ngOnInit() {
    this.initForm();
    this.patchFormValues();
    this.generateTimeOptions();
  }

  initForm() {
    this.workingDays()?.days.forEach(day => {
      this.getWorkingDaysFormGroup().addControl(
        day.name.value,
        new FormGroup({
          name: new FormControl(day.name.value),
          is_working: new FormControl(day.is_working),
          started_at: new FormControl(day.started_at),
          ended_at: new FormControl(day.ended_at),
        })
      );
    });
  }

  generateTimeOptions() {
    this.dropdownTimeOptions = [];

    // Generate time options from 6 AM to 9 PM
    for (let hour = 6; hour <= 21; hour++) {
      // Format the hour for 24-hour format value
      const hourStr = hour.toString().padStart(2, '0');
      const time24 = `${hourStr}:00`;

      // Apply dynamic timezone adjustment to get Saudi Arabia time
      const valueHour = Math.floor(
        (hour - this.currentTimezoneOffsetHours + 24) % 24
      );
      const valueHourStr = valueHour.toString().padStart(2, '0');
      const valueTime24 = `${valueHourStr}:00`;

      // Get the 12-hour format for the label
      const time12 = this.convertTo12Hour(time24);

      this.dropdownTimeOptions.push({
        label: time12,
        value: valueTime24,
      });
    }
  }

  convertTo12Hour(time: string): string {
    if (!time) return '';

    const [hourStr, minuteStr] = time.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr || '00';

    let amPm = hour >= 12 ? 'PM' : 'AM';
    if (this.currentLang === 'ar') {
      amPm = hour >= 12 ? 'م' : 'ص';
    }
    hour = hour % 12;
    hour = hour ?? 12; // Convert 0 to 12

    return `${hour}:${minute} ${amPm}`;
  }

  getWorkingDaysFormGroup() {
    return this.form.get('days') as FormGroup;
  }
  getDayFormGroup(day: string) {
    return this.getWorkingDaysFormGroup().get(day) as FormGroup;
  }
  returnSelectedWorkingDays() {
    const formValues = this.getWorkingDaysFormGroup().getRawValue();
    const labelValueArray: any[] = [];

    Object.values(formValues).forEach((day: any) => {
      if (day.is_working) {
        const dayName = workingDays.days.find(
          d => d.name.value === day.name
        )?.name;
        if (dayName) {
          labelValueArray.push({
            label: this.translocoService.translate(
              `system_settings_days_{${dayName.value}}_label`
            ),
            value: dayName.value,
          });
        }
      }
    });
    return labelValueArray;
  }

  resetFormControl(formControl: string) {
    this.form.get(formControl)?.reset();
  }

  checkStartDayIncludedInWorkingDays() {
    const startDay = this.form.get('start_day')?.value;
    const workingDays = this.returnSelectedWorkingDays();
    const filteredArray = workingDays.filter(day => day.value == startDay);
    if (filteredArray.length == 0) {
      this.resetFormControl('start_day');
    }
  }

  patchFormValues() {
    // Patch the main form values
    this.form.patchValue({
      start_day: this.workingDays()?.start_day.value,
      started_at: this.workingDays()?.days[0].started_at,
      ended_at: this.workingDays()?.days[0].ended_at,
    });

    // Patch each day's values to ensure checkboxes update
    this.workingDays()?.days.forEach(day => {
      const dayGroup = this.getDayFormGroup(day.name.value);
      if (dayGroup) {
        dayGroup.patchValue({
          name: day.name.value,
          is_working: day.is_working,
          started_at: day.started_at,
          ended_at: day.ended_at,
        });
      }
    });
  }

  timeCheck() {
    const startedAt = this.form.get('started_at')?.value;
    const endedAt = this.form.get('ended_at')?.value;

    if (startedAt && endedAt && endedAt <= startedAt) {
      this.form.get('ended_at')?.setErrors({ timeRange: true });
      this.form.markAllAsTouched();
      this.isLoading.set(false);
    }
  }

  submit() {
    this.isLoading.set(true);

    // Check if ended_at is smaller than started_at
    this.timeCheck();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.isLoading.set(false);
    } else {
      this.isLoading.set(true);
      const formValues = this.form.getRawValue();
      const days = Object.values(formValues.days);
      formValues.days = days.map((day: any) => ({
        ...day,
        started_at: formValues.started_at,
        ended_at: formValues.ended_at,
      }));

      this.updateWorkingDays(formValues);
    }
  }

  updateWorkingDays(formValues: any) {
    this.systemSettingsService.updateWorkingDays(formValues).subscribe({
      next: () => {
        this.router.navigate([
          this.tenantService.adminPath(
            `${ADMIN_ROUTES.SYSTEM_SETTINGS.PROFILE}/view`
          ),
        ]);
        this.isLoading.set(false);
      },
      error: (error: any) => {
        this.isLoading.set(false);
        this.toastService.add(
          error.error.message,
          '',
          'assets/images/icons/error.svg',
          'error'
        );
      },
    });
  }
}
